import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';

// 1,25 se lit « 1 h 15 » et 0,5 « 30 min », comme partout ailleurs dans le temps de travail.
const duree = (heures) => {
  const minutes = Math.round(heures * 60);
  const reste = minutes % 60;
  if (minutes < 60) return `${minutes} min`;
  return reste ? `${Math.floor(minutes / 60)} h ${String(reste).padStart(2, '0')}` : `${minutes / 60} h`;
};

// Le chargement se fait une fois, au montage : son message par défaut se
// traduit au rendu, pour ne pas faire dépendre la lecture de `t`.
const ERREUR_CHARGEMENT = 'chargement';

/**
 * Les heures supplémentaires calculées au pointage qui attendent une décision
 * (Yros/Kavero#491). Odoo calcule l'écart du jour ; le validateur de la
 * société (à défaut, la RH) le valide ou le refuse. Seules les heures
 * validées comptent. Refuser demande un motif, que le salarié lira.
 *
 * Monté dans l'onglet RH, et dans « Mon équipe » pour un validateur au compte
 * salarié : `onCompte` y tient le compteur des demandes, et `masquerSiVide`
 * évite un bloc vide sous « Aucune demande en attente ».
 */
function HeuresSupPointage({ onCompte, masquerSiVide = false }) {
  const { t, i18n } = useTranslation();
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(null);
  const [refusPour, setRefusPour] = useState(null);
  const [motif, setMotif] = useState('');

  useEffect(() => {
    if (onCompte && lignes !== null) onCompte(lignes.length);
  }, [lignes, onCompte]);

  useEffect(() => {
    let actif = true;
    Promise.resolve()
      .then(() => apiService.lireHeuresSupPointage())
      .then((reponse) => { if (actif) setLignes(reponse?.heures_sup || []); })
      .catch((e) => { if (actif) { setLignes([]); setErreur(e?.response?.data?.message || ERREUR_CHARGEMENT); } });
    return () => { actif = false; };
  }, []);

  const jour = (iso) => new Intl.DateTimeFormat(i18n?.language || 'fr-FR',
    { weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(`${iso}T00:00:00`));

  const decider = async (ligne, approuver, motifSaisi = undefined) => {
    setEnvoi(ligne.id);
    setErreur(null);
    try {
      await apiService.deciderHeuresSupPointage(ligne.id, approuver, motifSaisi);
      setLignes((liste) => liste.filter((l) => l.id !== ligne.id));
      setRefusPour(null);
      setMotif('');
    } catch (e) {
      setErreur(e?.response?.data?.message || t('heuresSupPointage.erreurDecision'));
    } finally {
      setEnvoi(null);
    }
  };

  if (masquerSiVide && lignes !== null && lignes.length === 0 && !erreur) return null;

  return (
    <section data-testid="heures-sup-pointage" className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <div>
        <h3 className="text-base font-semibold text-gray-800">{t('heuresSupPointage.titre')}</h3>
        <p className="text-sm text-gray-500">{t('heuresSupPointage.description')}</p>
      </div>
      {erreur && (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {erreur === ERREUR_CHARGEMENT ? t('heuresSupPointage.erreur') : erreur}
        </p>
      )}
      {lignes === null && <p className="text-sm text-gray-500">{t('heuresSupPointage.chargement')}</p>}
      {lignes && lignes.length === 0 && !erreur && (
        <p data-testid="heures-sup-vide" className="text-sm text-gray-500">{t('heuresSupPointage.vide')}</p>
      )}
      {lignes && lignes.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {lignes.map((ligne) => (
            <li key={ligne.id} data-testid={`heures-sup-${ligne.id}`} className="py-3 space-y-2">
              {/* Le nom, puis le jour et la durée ; les boutons dessous à droite,
                  comme les cartes de demande : la ligne tient sur un téléphone. */}
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="font-medium text-gray-800 min-w-0">{ligne.salarie.nom}</span>
                <span className="flex items-baseline gap-3 shrink-0">
                  <span className="text-sm text-gray-500 capitalize">{jour(ligne.date)}</span>
                  <span className="text-sm font-semibold text-emerald-700 tabular-nums">
                    {t('heuresSupPointage.heures', { duree: duree(ligne.heures) })}
                  </span>
                </span>
              </div>
              {refusPour !== ligne.id && (
                <div className="flex justify-end gap-2">
                  <button type="button" data-testid={`heures-sup-refuser-${ligne.id}`} disabled={envoi !== null}
                    onClick={() => { setRefusPour(ligne.id); setMotif(''); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                    <FaTimes aria-hidden="true" />{t('heuresSupPointage.refuser')}
                  </button>
                  <button type="button" data-testid={`heures-sup-valider-${ligne.id}`} disabled={envoi !== null}
                    onClick={() => decider(ligne, true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50">
                    <FaCheck aria-hidden="true" />{t('heuresSupPointage.valider')}
                  </button>
                </div>
              )}
              {refusPour === ligne.id && (
                <div className="flex flex-wrap items-center gap-2">
                  <input data-testid="heures-sup-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                    placeholder={t('heuresSupPointage.motif')} aria-label={t('heuresSupPointage.motif')}
                    className="flex-1 min-w-[12rem] px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
                  <button type="button" onClick={() => setRefusPour(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:underline">
                    {t('heuresSupPointage.annuler')}
                  </button>
                  <button type="button" data-testid="heures-sup-confirmer-refus" disabled={!motif.trim() || envoi !== null}
                    onClick={() => decider(ligne, false, motif.trim())}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                    {t('heuresSupPointage.confirmerRefus')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default HeuresSupPointage;
